import React, {
  useEffect,
  useRef,
  useState,
  CSSProperties,
  useCallback,
  useLayoutEffect,
} from 'react';

import TextareaBase from '../TextareaStyle';
import LabelSet from '@components/dataEntry/Input/Label/LabelSet';

interface TextareaProps {
  value: string;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement> | undefined;
  placeholder?: string;
  name?: string;
  label?: string;
  required?: boolean;
  isValid?: boolean;
  maxLength?: number;
  minRows?: number;
  maxRows?: number;
  autoRows?: boolean;
  minWidth?: number;
  maxWidth?: number;
  isfocus?: boolean;
  transparent?: boolean;
  style?: CSSProperties;
  textareaStyle?: CSSProperties;
  id?: string;
  customHeight?: string;
  description?: string;
  infoLink?: IinfoLink;
  align?: 'left' | 'center' | 'right';
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  onCompositionStart?: React.CompositionEventHandler<HTMLTextAreaElement>;
  onCompositionEnd?: React.CompositionEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
}

function AutoTextarea({
  name,
  label,
  placeholder,
  value,
  required,
  isValid,
  customHeight,
  maxLength = 500,
  minWidth = 100,
  maxWidth = 500,
  minRows = 1,
  maxRows = 8,
  autoRows,
  isfocus,
  onChange,
  style,
  textareaStyle,
  transparent,
  id,
  description,
  infoLink,
  align = 'left',
  onFocus,
  onCompositionStart,
  onCompositionEnd,
  onBlur,
}: TextareaProps) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [rows, setRows] = useState(minRows);
  const [height, setHeight] = useState('auto');

  // 라인 높이 상수
  const lineHeight = 24;

  // ✅ minRows를 고려한 높이 계산
  const handleTextareaHeight = useCallback(() => {
    if (textAreaRef.current) {
      if (customHeight) {
        textAreaRef.current.style.height = customHeight;
        return;
      }

      // autoRows가 true일 때만 자동 높이 조정
      if (autoRows) {
        textAreaRef.current.style.height = 'auto';

        // ✅ DOM이 업데이트된 후 scrollHeight 계산
        requestAnimationFrame(() => {
          if (textAreaRef.current) {
            const scrollHeight = textAreaRef.current.scrollHeight;

            // 최소/최대 높이 계산
            const minHeight = minRows * lineHeight;
            const maxHeight = maxRows * lineHeight;

            // ✅ 최소 높이를 보장하면서 최대 높이 제한
            const finalHeight = Math.max(
              minHeight, // ← 최소 높이 보장
              Math.min(scrollHeight, maxHeight),
            );

            textAreaRef.current.style.height = `${finalHeight}px`;
          }
        });
      } else {
        // autoRows가 false면 minRows 기본 높이 사용
        const minHeight = minRows * lineHeight;
        textAreaRef.current.style.height = `${minHeight}px`;
      }
    }
  }, [value, customHeight, autoRows, minRows, maxRows, lineHeight]);

  // ✅ DOM 렌더링 후 즉시 높이 계산 (초기 렌더링용)
  useLayoutEffect(() => {
    handleTextareaHeight();
  }, [handleTextareaHeight]);

  // ✅ value 변경 시 추가 높이 계산 (내용 변경용)
  useEffect(() => {
    if (value && autoRows) {
      // 값이 있고 autoRows일 때 추가 높이 재계산
      setTimeout(() => {
        handleTextareaHeight();
      }, 0);
    }
  }, [value, autoRows, handleTextareaHeight]);

  // 포커스 처리
  useEffect(() => {
    if (textAreaRef.current && isfocus) {
      textAreaRef.current.focus();
    }
  }, [isfocus]);

  const onChangeHandler = (e: any) => {
    onChange?.(e);

    // autoRows가 true일 때만 실시간 높이 조정
    if (autoRows) {
      // 다음 렌더링 사이클에서 높이 재계산
      setTimeout(() => {
        handleTextareaHeight();
      }, 0);
    }
  };

  return (
    <div style={{ width: '100%', ...style }}>
      {label && (
        <LabelSet
          required={required}
          infoLink={infoLink}
          label={label}
          description={description}
          align={align}
        />
      )}
      <TextareaBase
        id={id}
        value={value}
        name={name}
        placeholder={placeholder}
        isValid={isValid}
        ref={textAreaRef}
        onChange={onChangeHandler}
        onFocus={onFocus}
        onCompositionStart={onCompositionStart}
        onCompositionEnd={onCompositionEnd}
        onBlur={onBlur}
        rows={autoRows ? minRows : rows}
        maxLength={maxLength}
        transparent={transparent}
        style={{
          width: '100%',
          minHeight: `${minRows * lineHeight}px`,
          ...textareaStyle,
        }}
      />
    </div>
  );
}

export default AutoTextarea;
